/**
    * Layers management mixin
    * Provides functionality for managing layers in the Mikomic editor
 */
export default {
    data() {
        return {

            selected_layer: null,
            selected_blend_mode: 'normal',
            selected_opacity: 100,
            selected_opacity_text: '100%',
            show_opacity_slider: false,
            dragged_layer: null,
            drop_target_index: null,
            is_text_editing: false, 
            editing_layer: null, 

        };
    }, 

    computed: {
        panel_layers() {
            //console.log('panel_layers recomputed');
            let panel = this.selected_panel;
            
            // If we have a selected object, find its parent panel
            if (this.selected_object) {
                panel = this.find_parent_panel_for_object(this.selected_object);
            }
            // If no object is selected, check if a panel is selected (should be the global one after load/deselect)
            else if (this.selected_panel) {
                panel = this.selected_panel;
            }
            // As a fallback (e.g., during initial load before selected_panel is set), explicitly find the global panel
            else {
                panel = this.panels.find(p => p.getAttr('is_global') === true);
            }

            // If no panel could be determined (neither selected object nor selected panel exists, and no global panel found)
            if (!panel) {
                //console.log("panel_layers: No panel determined.");
                return [];
            }
            // Get all relevant objects (images, balloons, and standalone text) in a single query
            const all_objects = panel.content_group.find('.panel_image, .panel_balloon, .standalone_text');

            // Sort the objects based on their actual zIndex BEFORE mapping
            // Higher zIndex means visually on top
            all_objects.sort((a, b) => a.zIndex() - b.zIndex());
            
            // Return array of layer objects with necessary data
            return all_objects.map((obj) => {
                // Generate a thumbnail preview URL if possible
                const object_type = obj.getAttr('object_type') || 'image';
                let preview_url = '';

                if (object_type === 'image' && obj.image()) {
                    try {
                        var img = obj;
                        // Create a properly sized thumbnail of the image
                        const canvas = document.createElement('canvas');
                        const preview_size = 40; // Increased size for better preview
                        
                        // Get image dimensions
                        const img_width = img.image().width;
                        const img_height = img.image().height; 
                        
                        // Calculate scaled dimensions preserving aspect ratio
                        let scaled_width, scaled_height;
                        
                        if (img_width > img_height) {
                            scaled_width = preview_size;
                            scaled_height = (img_height / img_width) * preview_size;
                        } else {
                            scaled_height = preview_size;
                            scaled_width = (img_width / img_height) * preview_size;
                        }
                        
                        // Set canvas to standard size
                        canvas.width = preview_size;
                        canvas.height = preview_size;
                        
                        const ctx = canvas.getContext('2d');
                        
                        // Clear with transparent background
                        ctx.clearRect(0, 0, preview_size, preview_size);
                        
                        // Center the image in the canvas
                        const x_offset = (preview_size - scaled_width) / 2;
                        const y_offset = (preview_size - scaled_height) / 2;
                        
                        // Draw the image with proper scaling
                        ctx.drawImage(
                            img.image(), 
                            0, 0, img_width, img_height,
                            x_offset, y_offset, scaled_width, scaled_height
                        );
                        
                        preview_url = canvas.toDataURL('image/png');
                    } catch (e) {
                        // Ignore errors, preview is optional
                        console.log("Error creating thumbnail:", e);
                    }
                }

                else if (object_type === 'balloon') {
                    // Generate preview for balloon
                    try {
                        const canvas = document.createElement('canvas');
                        const preview_size = 40;
                        canvas.width = preview_size;
                        canvas.height = preview_size;
                        
                        const ctx = canvas.getContext('2d');
                        ctx.fillStyle = obj.shape ? obj.shape.fill() : '#FFFFFF';
                        ctx.strokeStyle = obj.shape ? obj.shape.stroke() : '#000000';
                        ctx.lineWidth = 2;
                        
                        // Draw simplified balloon
                        ctx.beginPath();
                        ctx.ellipse(preview_size/2, preview_size/2, preview_size/2 - 4, preview_size/2 - 8, 0, 0, 2 * Math.PI);
                        ctx.fill();
                        ctx.stroke();
                        
                        preview_url = canvas.toDataURL('image/png');
                    } catch (e) {
                        console.log("Error creating balloon thumbnail:", e);
                    }
                }

                else if (object_type === 'standalone_text') {
                    // Generate preview for standalone text
                    try {
                        const canvas = document.createElement('canvas');
                        const preview_size = 40;
                        canvas.width = preview_size;
                        canvas.height = preview_size;
                        
                        const ctx = canvas.getContext('2d');
                        ctx.fillStyle = '#1f2937';
                        //ctx.fillRect(0, 0, preview_size, preview_size);
                        
                        // Draw text icon
                        ctx.fillStyle = '#BBBBBB';
                        ctx.font = 'bold 24px Arial';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillText('T', 19, 20);
                        
                        preview_url = canvas.toDataURL('image/png');
                    } catch (e) {
                        console.log("Error creating text thumbnail:", e);
                    }
                }
                  // Use persistently stored name and locked state
                const layer_name = obj.getAttr('layer_name') || `${object_type === 'balloon' ? 'Balloon' : object_type === 'standalone_text' ? 'Text' : 'Layer'} ${obj.id().substring(obj.id().length - 7)}`; // Improved default naming
                const is_locked = obj.getAttr('locked') === true;
                
                return {
                    id: obj.id(),
                    name: layer_name,
                    node: obj,
                    visible: obj.visible(),
                    locked: is_locked,
                    preview_url: preview_url,
                    is_selected: this.selected_object === obj,
                    type: object_type
                };
            }).reverse(); // Reverse to show highest zIndex (top layer) at the top of the list (index 0)
        }
    },
    
    methods: {

        select_layer(layer) {

            // Check if layer visibility is off and show warning
            if (!layer.visible) {
                this.show_error_message(
                    this.get_t('layers.layer_not_visible'), 
                    this.get_t('layers.layer_hidden_message')
                );
                return;
            }            

            this.selected_layer = layer;

            // Change to select tool if not already active
            if (this.active_tool !== 'select') {
                this.change_tool('select'); 
            }

            // Select the object node - blend mode and opacity will be handled there
            this.select_object(layer.node);        
            
        },        
        
        toggle_layer_visibility(layer) {
            layer.visible = !layer.visible;
            layer.node.visible(layer.visible);

            //if visibility off, deselect the object
            if (!layer.visible && this.selected_object && this.selected_object.id() === layer.id) {
                this.deselect_object();
            }

            this.$forceUpdate();
            this.panels_layer.batchDraw();
            this.refresh_lucide();
            this.push_to_history('toggle_layer_visibility');
        },

        toggle_layer_lock(layer) {
            if (!layer || !layer.node) return;
            
            // Toggle the locked state
            const new_locked_state = !layer.locked;
            layer.locked = new_locked_state;
            
            // Store the locked state in the Konva node
            layer.node.setAttr('locked', new_locked_state);
            
            // Control event listening - locked layers should not capture clicks
            layer.node.listening(!new_locked_state);
            
            // If locking a selected object, deselect it
            if (new_locked_state && this.selected_object && this.selected_object.id() === layer.id) {
                this.deselect_object();
            }
            // If unlocking a layer, select it automatically
            else if (!new_locked_state) {
                this.select_layer(layer);
            }
            
            // Update the draggable state if in select mode
            if (this.active_tool === 'select') {
                layer.node.draggable(!new_locked_state);
            }
            
            // Record in history
            this.push_to_history('toggle_layer_lock');
            
            // Update the UI
            this.$forceUpdate();
            this.refresh_lucide();
        },

        async load_image_to_selected_panel(){
            await this.load_image_into_panel();
        },

        create_global_layer(){
            const panel = new Konva.Group({
                 x: 0,
                 y: 0,
                 name: 'global_panel_group',
                 id: 'mikomic_global_layer',
                 listening: true						   
             });

            // Add panel attribute for identification
            panel.setAttr('is_panel', true);

            const points = [
                 0, 0,
                 this.canvas_width, 0,
                 this.canvas_width, this.canvas_height,
                 0, this.canvas_height
             ];

             const content_group = new Konva.Group({
                name: 'content_group',
                listening: true // Ensure group listens for events
            });

            // Set clip function for content
            content_group.clipFunc((ctx) => {
                ctx.beginPath();
                for (let i = 0; i < points.length; i += 2) {
                    const x = points[i];
                    const y = points[i + 1];
                    if (i === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.closePath();
            });

            panel.add(content_group);

            panel.content_group = content_group;
            panel.points = points;
            panel.setAttr('is_global', true);

            this.panels_layer.add(panel);
            this.panels.push(panel);

            panel.moveToTop();
        },

        edit_layer_name(layer) {
            if (!layer || !layer.node) return;
            
            // Crear elemento input temporal para edición
            const input = document.createElement('input');
            input.type = 'text';
            input.value = layer.name;
            input.className = 'bg-gray-700 text-white text-s border border-gray-600 rounded px-1 py-0.5 w-full';
            input.style.userSelect = 'text'; // Permitir selección de texto solo en el input
            
            // Encontrar el elemento de nombre de capa a reemplazar usando el data-layer-id
            const layer_items = document.querySelectorAll('#right_bar [data-layer-id]');
            let layer_item = null;
            
            for (const item of layer_items) {
                // Buscar por data-layer-id que coincida exactamente
                if (item.dataset.layerId === layer.id) {
                    layer_item = item;
                    break;
                }
            }
            
            if (!layer_item) return;
            
            // Encontrar el elemento de texto dentro del item de capa
            const text_element = layer_item.querySelector('.flex-1');
            if (!text_element) return;
            
            // Guardar el HTML original para restauración si es necesario
            const original_html = text_element.innerHTML;
            
            // Reemplazar con el input
            text_element.innerHTML = '';
            text_element.appendChild(input);
            
            // Marcar que estamos en modo de edición de texto
            this.is_text_editing = true;
            
            // Almacenar referencia de capa para contexto de edición
            this.editing_layer = {
                layer,
                input,
                text_element,
                original_html,
                confirmed: false, // Flag para evitar procesamiento duplicado
                cancelled: false // Flag para evitar procesamiento duplicado
            };
            
            // Enfocar y seleccionar todo el texto
            input.focus();
            input.select();
            
            // Agregar manejador de blur que aplica cambios al hacer clic fuera
            input.addEventListener('blur', () => {
                // Solo confirmar si no se ha confirmado o cancelado previamente
                if (this.editing_layer && !this.editing_layer.confirmed && !this.editing_layer.cancelled) {
                    this.editing_layer.confirmed = true;
                    this.confirm_layer_name_edit();
                }
            });
        },

        confirm_layer_name_edit() {
            if (!this.editing_layer) return;
            
            const { layer, input, text_element } = this.editing_layer;
            
            try {
                // Verificar si ya se procesó el input (por evento blur)
                if (!input.isConnected || !input.parentNode) {
                    this.is_text_editing = false;
                    this.editing_layer = null;
                    return;
                }
                
                // Obtener el nuevo nombre, usar el original si está vacío
                const new_name = input.value.trim() || layer.name;
                
                // Actualizar el nombre en el modelo de datos
                layer.name = new_name;
                
                // Actualizar el atributo almacenado en el nodo Konva
                layer.node.setAttr('layer_name', new_name);
                
                // Actualizar texto de forma segura sin usar innerHTML
                try {
                    // Desconectar el input antes de actualizar el texto
                    text_element.removeChild(input);
                } catch (e) {
                    // Ignorar error si el input ya fue eliminado por el evento blur
                    console.log("Input already removed, continuing with update");
                }
                
                // Establecer el contenido de texto directamente
                text_element.textContent = new_name;
                
                // Salir del modo de edición
                this.is_text_editing = false;
                this.editing_layer = null;
                
                // Registrar cambio en el historial
                this.push_to_history('edit_layer_name');
            } catch (e) {
                console.warn("Warning during layer name confirmation:", e);
                // Asegurar que siempre salimos del modo de edición
                this.is_text_editing = false;
                this.editing_layer = null;
            }
        },
        
        cancel_layer_name_edit() {
            if (!this.editing_layer) return;
            
            try {
                // Marcar como cancelado para que el evento blur no lo procese
                this.editing_layer.cancelled = true;
                
                const { text_element, original_html, input } = this.editing_layer;
                
                // Eliminar el input de forma segura si aún existe
                if (input && input.parentNode === text_element) {
                    text_element.removeChild(input);
                }
                
                // Restaurar el contenido original de forma segura
                text_element.textContent = '';
                const temp = document.createElement('div');
                temp.innerHTML = original_html;
                while (temp.firstChild) {
                    text_element.appendChild(temp.firstChild);
                }
            } catch (e) {
                console.warn("Error during layer name cancellation:", e);
            } finally {
                // Asegurar que siempre salimos del modo de edición
                this.is_text_editing = false;
                this.editing_layer = null;
            }
        },
        delete_layer(layer) {
            // Check if layer is locked
            if (layer.locked) {
                // Show message that locked layers can't be deleted
                this.show_popup_with_content(
                    this.get_t('layers.cannot_delete_layer'), 
                    '<div class="mb-4 text-center"><p class="text-s">' + this.get_t('layers.layer_locked_delete_message') + '</p></div>'
                );
                return;
            }

            if (layer.node) {
                layer.node.destroy();
            }
            
            // If this was the selected layer, deselect it
            if (this.selected_layer && this.selected_layer.id === layer.id) {
                this.selected_layer = null;
            }
            
            // If this was the selected object, deselect it
            if (this.selected_object && this.selected_object.id() === layer.id) {
                this.deselect_object();
            }
            
            
            // Force panel_layers computed to recalculate by temporarily changing selected_panel
            const current_panel = this.selected_panel;
            this.selected_panel = null;
            this.$nextTick(() => {
                this.selected_panel = current_panel;
                this.$forceUpdate();
                this.panels_layer.batchDraw();
            });

            
            
            // Only record in history if this is not part of a cut operation cleanup
            if (!this.is_performing_cut_cleanup) {
                this.push_to_history('delete_layer');
            }
            
            return; 
            
        },
        
        // Methods for handling layer drag and drop
        start_drag_layer(layer, event) { 
            this.dragged_layer = layer;

            //select layer
            this.select_layer(layer, event);
            
            // Add dragging class to html element to affect all cursors
            document.documentElement.classList.add('dragging');
            
            // Para mejorar la experiencia visual durante el arrastre
            if (event.dataTransfer) {
                // Establecer datos necesarios para el arrastre
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', layer.id);
                
                // Si hay una miniatura disponible, usarla como drag image
                if (layer.preview_url) {
                    const img = new Image();
                    img.src = layer.preview_url;
                    event.dataTransfer.setDragImage(img, 15, 15);
                }
            }
        },

        // Unified drag_layer_over method to handle all indicator positioning correctly
        drag_layer_over(target_layer, event) {
            if (!this.dragged_layer) return;
            
            event.preventDefault();
            
            // Get container and indicator
            const container = event.currentTarget.closest('[class*="overflow-y-auto"]');
            if (!container) return;
            
            const indicator = this.$refs.layer_drop_indicator;
            if (!indicator) return;
            
            // Get source index
            const source_index = this.panel_layers.findIndex(l => l.id === this.dragged_layer.id);
            
            // Always reset indicator first
            indicator.classList.add('hidden');
            indicator.style.position = '';
            indicator.style.top = '';
            indicator.style.left = '';
            indicator.style.pointerEvents = 'none';
            
            // Remove from DOM to prevent duplicates
            if (indicator.parentNode) {
                indicator.parentNode.removeChild(indicator);
            }
            
            // Case 1: Dragging over a layer
            if (target_layer) {
                const target_index = this.panel_layers.findIndex(l => l.id === target_layer.id);
                const rect = event.currentTarget.getBoundingClientRect();
                const y = event.clientY - rect.top;
                const is_below = y > rect.height / 2;
                
                // Skip if trying to place at the same position
                if (this.dragged_layer.id === target_layer.id || 
                    (source_index === target_index) || 
                    (source_index === target_index + 1 && is_below) || 
                    (source_index === target_index - 1 && !is_below)) {
                    
                    this.drop_target_index = null;
                    return;
                }
                
                // Calculate drop position
                this.drop_target_index = is_below ? target_index + 1 : target_index;
                
                // Get all layer elements
                const layer_elements = Array.from(container.querySelectorAll('[data-layer-id]'));
                
                // Find target element
                const target_element = layer_elements.find(el => el.dataset.layerId === target_layer.id);
                if (!target_element) return;

                indicator.style.position = 'absolute';
                indicator.style.left = '0';
                indicator.style.width = '100%';
                indicator.style.pointerEvents = 'none';
                
                // Insert indicator at correct position in the DOM
                if (is_below) {
                    // Insert after target element
                    if (target_element.nextSibling) {
                        target_element.parentNode.insertBefore(indicator, target_element.nextSibling);
                    } else {
                        target_element.parentNode.appendChild(indicator);
                    }
                } else {
                    // Insert before target element
                    target_element.parentNode.insertBefore(indicator, target_element);
                }
            }
            // Case 2: Dragging over empty space or edges
            else {
                const container_rect = container.getBoundingClientRect();
                const y = event.clientY - container_rect.top;
                const layer_elements = Array.from(container.querySelectorAll('[data-layer-id]'));
                
                // Top edge
                if (y < 20) {
                    // Skip if already at top
                    if (source_index === this.panel_layers.length - 1) {
                        this.drop_target_index = null;
                        return;
                    }
                    
                    this.drop_target_index = this.panel_layers.length - 1;
                    
                    // Insert at the beginning
                    if (layer_elements.length > 0) {
                        container.insertBefore(indicator, layer_elements[0]);
                    } else {
                        container.appendChild(indicator);
                    }
                }
                // Bottom edge
                else if (y > container_rect.height - 20) {
                    // Skip if already at bottom
                    if (source_index === 0) {
                        this.drop_target_index = null;
                        return;
                    }
                    
                    this.drop_target_index = 0;
                    
                    // Insert at the end
                    container.appendChild(indicator);
                }
                else {
                    // Middle area with no specific target
                    this.drop_target_index = null;
                    return;
                }
            }
            
            // Style the indicator
            indicator.style.height = '2px';
            indicator.style.backgroundColor = '#ec4899';  // rose-500
            indicator.style.pointerEvents = 'none';
            indicator.classList.remove('hidden');
        },
        
        // Updated drop handler to work with the unified approach
        drop_layer(target_layer, event) {
            event.preventDefault();
            
            // Hide and reset indicator
            const indicator = this.$refs.layer_drop_indicator;
            if (indicator) {
                indicator.classList.add('hidden');
                indicator.style.position = '';
                indicator.style.top = '';
                indicator.style.left = '';
                indicator.style.pointerEvents = 'none';
            }
            
            if (!this.dragged_layer || !this.selected_panel || this.drop_target_index === null) {
                this.dragged_layer = null;
                this.drop_target_index = null;
                return;
            }
            
            // Get source index
            const source_index = this.panel_layers.findIndex(l => l.id === this.dragged_layer.id);
            
            // Verify we're not trying to drop at the same position
            if (source_index === this.drop_target_index) {
                this.dragged_layer = null;
                this.drop_target_index = null;
                return;
            }
            
            // When dragging Layer 3 between Layer 1 and Layer 2, the condition below
            // was preventing the drop from working correctly
            // Removed: source_index === this.drop_target_index - 1
            
            // Reorder the layers
            this.reorder_layers(source_index, this.drop_target_index);
            
            // Reset drag state
            this.dragged_layer = null;
            this.drop_target_index = null;
            
        },


        drop_layer_on_container(event) {
            // Just call drop_layer with null target
            this.drop_layer(null, event);
        },

        reorder_layers(source_index, target_index) {
            if (!this.selected_panel) return;

            // Get content group
            const content_group = this.selected_panel.content_group;

            // Get all objects in the panel
            const panel_images = content_group.find('.panel_image');
            const panel_balloons = content_group.find('.panel_balloon');
            const all_objects = [...panel_images, ...panel_balloons];

            // Get the node we're moving
            // Ensure source_index is valid before accessing panel_layers
            if (source_index < 0 || source_index >= this.panel_layers.length) {
                console.error("Invalid source_index in reorder_layers:", source_index);
                return;
            }
            const moving_node = this.panel_layers[source_index].node;

            // Determine if we're moving a layer down
            // In panel_layers, index 0 is top layer, higher index is lower layer
            // So moving down means source_index < target_index
            const is_moving_down = source_index < target_index;

            // Adjust target index when moving down to fix the positioning issue
            // When moving down, we need to offset by +1 to get the correct position
            // Note: This adjustment logic might need review depending on how target_index is calculated/used.
            // Keeping user's current logic.
            const adjusted_target = is_moving_down ? target_index - 1 : target_index;

            // Create a new array with the correct order after the move
            const reordered_layers = [];

            // Copy layers to the new array, skipping the source layer
            for (let i = 0; i < this.panel_layers.length; i++) {
                if (i !== source_index) {
                    reordered_layers.push(this.panel_layers[i]);
                }
            }

            // Insert the source layer at the adjusted target position
            // Ensure adjusted_target is within bounds
            const final_target_index = Math.max(0, Math.min(adjusted_target, reordered_layers.length));
            reordered_layers.splice(final_target_index, 0, this.panel_layers[source_index]);

            // Now reordered_layers represents the new visual order
            // Set zIndex values to match the visual order (higher index = higher zIndex)
            let z_index_changed = false; // Track if zIndex actually changes
            for (let i = 0; i < reordered_layers.length; i++) {
                // Map visual position to zIndex:
                // - Top layer (index 0) gets highest zIndex (length-1)
                // - Bottom layer (index length-1) gets lowest zIndex (0)
                const zindex = reordered_layers.length - 1 - i;
                if (reordered_layers[i].node.zIndex() !== zindex) {
                    reordered_layers[i].node.zIndex(zindex);
                    z_index_changed = true;
                }
            }

            // Redraw and update history only if the order actually changed
            if (z_index_changed) {
                content_group.draw(); // User's original call
                this.redraw(); // User's original call
                this.push_to_history('reorder_layers'); // User's original call

                // --- Trigger reactivity for panel_layers ---
                // Add this block ONLY to force UI update after drop in loaded documents.
                const currently_selected_node = this.selected_object;
                if (currently_selected_node) {
                    this.selected_object = null;
                    this.$nextTick(() => {
                        this.selected_object = currently_selected_node;
                    });
                }
                // --- End reactivity trigger ---
            }
        },

        select_global_layers() { 
            // Deselect everything first
            this.deselect_all();
            
            // Find the global panel
            const global_panel = this.panels.find(panel => panel.getAttr('is_global'));
            
            if (global_panel) {
                // First select the global panel
                this.selected_panel = global_panel;
                
                // Update last_selected_panel to global if it was destroyed or invalid
                if (!this.last_selected_panel || !this.last_selected_panel.getLayer || !this.last_selected_panel.content_group) {
                    this.last_selected_panel = global_panel;
                }
                
                // Force the reactivity by temporarily clearing the selection
                // and then restoring it
                this.$nextTick(() => {
                    this.selected_panel = null; // Clear selection to force reactivity
                    
                    // Restore the global panel selection in the next tick
                    this.$nextTick(() => {
                        this.selected_panel = global_panel; 
                        this.redraw(); // Ensure visual updates
                    });
                });
            } else {
                console.warn("No global panel found");
            }
        },

        // Helper to get the next available layer name for a panel (images and balloons)
        get_next_layer_name(panel) {
            // Find all objects in the panel (images and balloons)
            const siblings = panel.content_group.find('.panel_image, .panel_balloon, .standalone_text');
            let max_number = 0; 
            siblings.forEach(layer => {
                const name = layer.getAttr('layer_name');
                if (name && name.startsWith('Layer ')) {
                    const num = parseInt(name.replace('Layer ', ''), 10);
                    if (!isNaN(num) && num > max_number) max_number = num;
                }
            });
            return `Layer ${max_number + 1}`;
        },

        // Get current layer count for a panel
        get_panel_layer_count(panel) {
            if (!panel || !panel.content_group) return 0;
            const layers = panel.content_group.find('.panel_image, .panel_balloon, .standalone_text');
            return layers.length;
        },

        

    }
};